import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GoogleLogin, CredentialResponse } from "@react-oauth/google";
import Webcam from "react-webcam";
import { jwtDecode } from "jwt-decode";
import {
  registerUser,
  clearError,
  clearRegistrationSuccess,
} from "../../redux/slices/authSlice";
import { addNotification } from "../../redux/slices/uiSlice";
import { AppDispatch, RootState } from "../../redux/store";
import {
  AuthProvider,
  EmailRegisterData,
  GoogleRegisterData,
  FaceRegisterData,
} from "../../types";
import {
  validateEmail,
  validatePassword,
  validateFirstName,
  validateLastName,
  getPasswordStrength,
} from "../../utils/validation.utils";
import { Button } from "../common/Button";
import { Input } from "../common/Input";
import { Spinner } from "../common/Spinner";
import { FaceFrame } from "./FaceFrame";
import { Mail, Chrome, ScanFace, User, Eye, EyeOff } from "lucide-react";

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

interface GoogleJwtPayload {
  email: string;
  given_name?: string;
  family_name?: string;
  name?: string;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSwitchToLogin,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error, registrationSuccess, lastAuthProvider } = useSelector(
    (state: RootState) => state.auth
  );

  const [authMethod, setAuthMethod] = useState<AuthProvider>("email");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Face auth state
  const webcamRef = useRef<Webcam>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  const passwordStrength = getPasswordStrength(password);

  // Handle successful registration
  useEffect(() => {
    if (registrationSuccess && lastAuthProvider) {
      dispatch(
        addNotification({
          type: "success",
          message: "Account created successfully! Please sign in.",
        })
      );
      dispatch(clearRegistrationSuccess());
      navigate(`/login?method=${lastAuthProvider}`);
    }
  }, [registrationSuccess, lastAuthProvider, dispatch, navigate]);

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!email) {
      errors.email = "Email is required";
    } else if (!validateEmail(email)) {
      errors.email = "Invalid email format";
    }

    if (!firstName) {
      errors.firstName = "First name is required";
    } else if (!validateFirstName(firstName)) {
      errors.firstName = "First name must be 1-50 characters";
    }

    if (lastName && !validateLastName(lastName)) {
      errors.lastName = "Last name must be 50 characters or less";
    }

    if (authMethod === "email") {
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        errors.password = passwordValidation.errors[0];
      }

      if (password !== confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email, firstName, lastName, authMethod, password, confirmPassword]);

  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());

    if (!validateForm()) return;

    const data: EmailRegisterData = {
      auth_provider: "email",
      email,
      first_name: firstName,
      last_name: lastName || undefined,
      password,
    };

    dispatch(registerUser(data));
  };

  const handleGoogleSuccess = (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      const decoded = jwtDecode<GoogleJwtPayload>(
        credentialResponse.credential
      );

      const data: GoogleRegisterData = {
        auth_provider: "google",
        google_token: credentialResponse.credential,
        email: decoded.email,
        first_name: decoded.given_name || decoded.name?.split(" ")[0] || "User",
        last_name:
          decoded.family_name || decoded.name?.split(" ").slice(1).join(" "),
      };

      dispatch(registerUser(data));
    }
  };

  const handleGoogleError = () => {
    setValidationErrors({ google: "Google sign-up failed. Please try again." });
  };

  const handleFaceCapture = useCallback(async () => {
    if (!validateForm()) return;
    if (!webcamRef.current) return;

    setIsCapturing(true);
    const imageSrc = webcamRef.current.getScreenshot();

    if (imageSrc) {
      const base64Data = imageSrc.split(",")[1];

      const data: FaceRegisterData = {
        auth_provider: "face",
        email,
        first_name: firstName,
        last_name: lastName || undefined,
        face_image: base64Data,
      };

      dispatch(registerUser(data));
    }

    setIsCapturing(false);
  }, [dispatch, email, firstName, lastName, validateForm]);

  const authMethods = [
    { id: "email" as AuthProvider, label: "Email", icon: <Mail className="w-4 h-4" /> },
    { id: "google" as AuthProvider, label: "Google", icon: <Chrome className="w-4 h-4" /> },
    { id: "face" as AuthProvider, label: "Face ID", icon: <ScanFace className="w-4 h-4" /> },
  ];

  const renderAuthMethodTabs = () => (
    <div className="flex gap-1.5 p-1 bg-slate-900/50 rounded-lg mb-4">
      {authMethods.map((method) => (
        <button
          key={method.id}
          type="button"
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 text-xs font-medium rounded-md transition-all ${
            authMethod === method.id
              ? "bg-gradient-to-r from-primary-500/20 to-accent-500/20 text-primary-400 border border-primary-500/30"
              : "text-slate-400 hover:text-slate-300 hover:bg-slate-800/50"
          }`}
          onClick={() => setAuthMethod(method.id)}
        >
          {method.icon}
          <span>{method.label}</span>
        </button>
      ))}
    </div>
  );

  const renderEmailForm = () => (
    <form onSubmit={handleEmailRegister} className="space-y-3">
      {/* Row 1: Email full width */}
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        error={validationErrors.email}
        disabled={loading}
        leftIcon={<Mail className="w-4 h-4" />}
      />

      {/* Row 2: First Name + Last Name */}
      <div className="grid grid-cols-2 gap-2">
        <Input
          label="First Name"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="John"
          error={validationErrors.firstName}
          disabled={loading}
          leftIcon={<User className="w-4 h-4" />}
        />
        <Input
          label="Last Name"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Doe"
          error={validationErrors.lastName}
          disabled={loading}
        />
      </div>

      {/* Row 3: Password + Confirm Password side by side */}
      <div className="grid grid-cols-2 gap-2">
        <Input
          label="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Min 8 characters"
          error={validationErrors.password}
          disabled={loading}
          rightIcon={
            <button
              type="button"
              className="text-slate-400 hover:text-slate-300 transition-colors"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          }
        />
        <Input
          label="Confirm"
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm"
          error={validationErrors.confirmPassword}
          disabled={loading}
          rightIcon={
            <button
              type="button"
              className="text-slate-400 hover:text-slate-300 transition-colors"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          }
        />
      </div>

      {/* Clean password strength indicator - full width bar */}
      {password && (
        <div className="flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                passwordStrength === "weak" ? "w-1/3 bg-red-500" :
                passwordStrength === "medium" ? "w-2/3 bg-amber-500" : "w-full bg-emerald-500"
              }`}
            />
          </div>
          <span className={`text-xs font-medium capitalize ${
            passwordStrength === "weak" ? "text-red-400" :
            passwordStrength === "medium" ? "text-amber-400" : "text-emerald-400"
          }`}>{passwordStrength}</span>
        </div>
      )}

      {error && (
        <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs">
          {error}
        </div>
      )}

      <Button type="submit" variant="gradient" fullWidth disabled={loading}>
        {loading ? <Spinner size="sm" /> : "Create Account"}
      </Button>
    </form>
  );

  const renderGoogleForm = () => (
    <div className="space-y-4 py-4">
      <p className="text-slate-400 text-xs text-center">
        Sign up instantly with Google
      </p>

      <div className="flex justify-center">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          theme="filled_black"
          size="large"
          width="260"
          text="signup_with"
        />
      </div>

      {(validationErrors.google || error) && (
        <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs text-center">
          {validationErrors.google || error}
        </div>
      )}
    </div>
  );

  const renderFaceForm = () => (
    <div className="space-y-3">
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        error={validationErrors.email}
        disabled={loading}
        leftIcon={<Mail className="w-4 h-4" />}
      />

      <div className="grid grid-cols-2 gap-2">
        <Input
          label="First Name"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="John"
          error={validationErrors.firstName}
          disabled={loading}
        />
        <Input
          label="Last Name"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Doe"
          error={validationErrors.lastName}
          disabled={loading}
        />
      </div>

      {/* Compact Webcam */}
      <div className="relative rounded-lg overflow-hidden bg-slate-900 w-full max-w-[180px] mx-auto aspect-[4/3] border border-slate-700/50">
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          videoConstraints={{ width: 180, height: 135, facingMode: "user" }}
          onUserMedia={() => setIsCameraReady(true)}
          onUserMediaError={() => setIsCameraReady(false)}
          className="w-full h-full object-cover"
        />
        <FaceFrame size="small" />
      </div>

      {error && (
        <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs text-center">
          {error}
        </div>
      )}

      <Button
        type="button"
        variant="gradient"
        fullWidth
        onClick={handleFaceCapture}
        disabled={loading || !isCameraReady || isCapturing}
        icon={<ScanFace className="w-4 h-4" />}
      >
        {loading || isCapturing ? <Spinner size="sm" /> : "Capture & Register"}
      </Button>

      <p className="text-[10px] text-slate-500 text-center">
        Demo purposes only
      </p>
    </div>
  );

  return (
    <div className="w-full">
      {/* Compact Header */}
      <div className="text-center mb-3">
        <h2 className="text-lg font-bold text-white">Create Account</h2>
        <p className="text-slate-400 text-xs">Join UniVision today</p>
      </div>

      {renderAuthMethodTabs()}

      {authMethod === "email" && renderEmailForm()}
      {authMethod === "google" && renderGoogleForm()}
      {authMethod === "face" && renderFaceForm()}

      <div className="mt-4 pt-4 border-t border-slate-700/50 text-center">
        <span className="text-slate-400 text-xs">Already have an account? </span>
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-primary-400 hover:text-primary-300 font-semibold text-xs transition-colors"
        >
          Sign In
        </button>
      </div>
    </div>
  );
};
